import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { register, me } from '@/api';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';
import { User, Mail, Lock, Phone, MapPin, ArrowLeft, Loader2 } from 'lucide-react';

const LOGO_URL = '/logo.png';

export default function RegisterClient(){
  const { t } = useLanguage();
  const { login: authLogin } = useAuth();
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [cap, setCap] = useState('');
  const [city, setCity] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await register({
        role: 'client',
        name,
        surname,
        email,
        password,
        phone,
        street,
        number,
        cap,
        city
      });
      const token = res.data.access_token;
      
      sessionStorage.setItem('jc_token', token);
      
      const meRes = await me();
      const userData = meRes.data;
      
      authLogin(token, userData);
      
      toast.success(t('registerClient.successMessage'));
      
      setTimeout(() => {
        nav('/cliente/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(t('registerClient.errorMessage'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
              <img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" />
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-2xl">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-6 sm:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {t('registerClient.title')}
              </h1>
              <p className="text-slate-500 mt-2">
                Crea il tuo account gratuito per pubblicare richieste
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-6">
              {/* Personal Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Dati Personali
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input 
                      placeholder={t('registerClient.name')}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      placeholder={t('registerClient.surname')}
                      value={surname}
                      onChange={e => setSurname(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contatti
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input 
                      type="email"
                      placeholder={t('registerClient.email')}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      type="tel"
                      placeholder="+41 ** *** ****"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Password
                </h3>
                <Input 
                  type="password"
                  placeholder={t('registerClient.password')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-slate-200"
                  required
                  minLength={6}
                />
                <p className="text-xs text-slate-500 mt-1">Minimo 6 caratteri</p>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Indirizzo
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  <div className="col-span-2 sm:col-span-2">
                    <Input 
                      placeholder={t('registerClient.street')}
                      value={street}
                      onChange={e => setStreet(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <Input 
                      placeholder="N°"
                      value={number}
                      onChange={e => setNumber(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <Input 
                      placeholder="CAP"
                      value={cap}
                      onChange={e => setCap(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-4">
                    <Input 
                      placeholder={t('registerClient.city')}
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full h-12 rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white font-semibold text-base shadow-lg shadow-indigo-200"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('registerClient.register')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                {t('auth.haveAccount')}{' '}
                <Link to="/login" className="text-[#5848FF] font-semibold hover:underline">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
